import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { doc, getDoc } from "firebase/firestore";
import {
    Activity,
    AlertCircle,
    AlertTriangle,
    Calendar,
    CheckCheck,
    CheckCircle,
    Clock,
    FileText,
    MapPin,
    Phone,
    PrinterIcon,
    Tag,
    Timer,
    User,
    Wifi,
    Server,
    Power,
    Signal
} from 'lucide-react';
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loading } from "../components/common/Loading";
import { db } from "../network/firebaseConfig";

dayjs.extend(relativeTime);

const ComplaintStatus = () => {
    const { id } = useParams();
    const [complaint, setComplaint] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [timeRemaining, setTimeRemaining] = useState(null);
    const [isDelayed, setIsDelayed] = useState(false);
    const [delayDuration, setDelayDuration] = useState(null);
    const [isPaused, setIsPaused] = useState(false);
    const [pauseRemaining, setPauseRemaining] = useState(null);

    useEffect(() => {
        const fetchComplaint = async () => {
            try {
                const docRef = doc(db, "complaints", id);
                const snap = await getDoc(docRef);

                if (snap.exists()) {
                    setComplaint({ id: snap.id, ...snap.data() });
                } else {
                    setError("Complaint not found!");
                }
            } catch (err) {
                console.error(err);
                setError("Failed to load complaint data.");
            } finally {
                setLoading(false);
            }
        };

        fetchComplaint();
    }, [id]);

    // Helper function to get value from onu/router (handle arrays)
    const getStatusValue = (value) => {
        if (Array.isArray(value)) {
            return value.length > 0 ? value[0] : '-';
        }
        return value || '-';
    };

    // Countdown timer logic with working hours (9AM to 10PM)
    useEffect(() => {
        if (!complaint || complaint.status !== 'in-operation') return;

        const calculateTime = () => {
            const hoursMatch = complaint.approximateResolveTime?.match(/(\d+)/);
            if (!hoursMatch) return;

            const totalWorkingHours = parseInt(hoursMatch[1]);
            const updatedAt = complaint.updatedAt?.toDate?.() || new Date(complaint.updatedAt);
            if (!updatedAt) return;

            const now = new Date();

            // Calculate working minutes between two dates efficiently
            const getWorkingMinutes = (startDate, endDate) => {
                // If start and end are the same day
                if (startDate.toDateString() === endDate.toDateString()) {
                    const startHour = startDate.getHours();
                    const startMinute = startDate.getMinutes();
                    const endHour = endDate.getHours();
                    const endMinute = endDate.getMinutes();

                    // Check if time range overlaps with working hours (9AM - 10PM)
                    const workStart = Math.max(startHour, 9);
                    const workEnd = Math.min(endHour, 22);

                    if (workStart < workEnd) {
                        let minutes = (workEnd - workStart) * 60;
                        // Adjust for minutes
                        if (startHour < 9) {
                            minutes -= startMinute;
                        } else {
                            minutes -= startMinute;
                        }
                        if (endHour > 22) {
                            minutes += (60 - endMinute);
                        } else {
                            minutes += (60 - endMinute);
                        }
                        return Math.max(0, minutes);
                    }
                    return 0;
                }

                // Different days - calculate full working hours for complete days
                let totalMinutes = 0;
                const start = new Date(startDate);
                const end = new Date(endDate);

                const startDay = new Date(startDate);
                startDay.setHours(0, 0, 0, 0);
                const endDay = new Date(endDate);
                endDay.setHours(0, 0, 0, 0);

                // Calculate full working days between start and end
                let currentDay = new Date(startDay);
                while (currentDay <= endDay) {
                    const dayStart = new Date(currentDay);
                    dayStart.setHours(9, 0, 0, 0);
                    const dayEnd = new Date(currentDay);
                    dayEnd.setHours(22, 0, 0, 0);

                    if (currentDay.getTime() === startDay.getTime()) {
                        // First day - only count from start time to 10PM
                        const actualStart = new Date(startDate);
                        const effectiveStart = actualStart > dayStart ? actualStart : dayStart;
                        if (effectiveStart < dayEnd) {
                            const minutes = (dayEnd - effectiveStart) / (1000 * 60);
                            totalMinutes += Math.max(0, minutes);
                        }
                    } else if (currentDay.getTime() === endDay.getTime()) {
                        // Last day - only count from 9AM to end time
                        const actualEnd = new Date(endDate);
                        const effectiveEnd = actualEnd < dayEnd ? actualEnd : dayEnd;
                        if (dayStart < effectiveEnd) {
                            const minutes = (effectiveEnd - dayStart) / (1000 * 60);
                            totalMinutes += Math.max(0, minutes);
                        }
                    } else {
                        // Full working day - 13 hours (9AM to 10PM)
                        totalMinutes += 13 * 60;
                    }

                    currentDay.setDate(currentDay.getDate() + 1);
                }

                return totalMinutes;
            };

            // Calculate working minutes between updatedAt and now
            const elapsedWorkingMinutes = getWorkingMinutes(updatedAt, now);
            const totalWorkingMinutesAvailable = totalWorkingHours * 60;
            const remainingWorkingMinutes = totalWorkingMinutesAvailable - elapsedWorkingMinutes;

            // Check if currently in working hours
            const currentHour = now.getHours();
            const currentMinute = now.getMinutes();
            const isWorkingHour = currentHour >= 9 && currentHour < 22;

            // If not in working hours and still has time remaining, show paused state
            if (!isWorkingHour && remainingWorkingMinutes > 0) {
                const nextWorkStart = new Date(now);
                nextWorkStart.setHours(9, 0, 0, 0);
                nextWorkStart.setDate(nextWorkStart.getDate() + 1);

                const pauseRemainingMs = nextWorkStart - now;
                const hours = Math.floor(pauseRemainingMs / (1000 * 60 * 60));
                const minutes = Math.floor((pauseRemainingMs % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((pauseRemainingMs % (1000 * 60)) / 1000);

                setPauseRemaining({ hours, minutes, seconds });
                setIsPaused(true);
                setTimeRemaining(null);
                setIsDelayed(false);
                setDelayDuration(null);
                return;
            }

            setIsPaused(false);
            setPauseRemaining(null);

            if (remainingWorkingMinutes > 0) {
                // Still have time remaining
                const remainingMs = remainingWorkingMinutes * 60 * 1000;
                const hours = Math.floor(remainingMs / (1000 * 60 * 60));
                const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((remainingMs % (1000 * 60)) / 1000);

                setTimeRemaining({ hours, minutes, seconds });
                setIsDelayed(false);
                setDelayDuration(null);
            } else {
                // Delayed - over time
                const delayMinutes = Math.abs(remainingWorkingMinutes);
                const hours = Math.floor(delayMinutes / 60);
                const minutes = Math.floor(delayMinutes % 60);

                setTimeRemaining(null);
                setIsDelayed(true);
                setDelayDuration({ hours, minutes, seconds: 0 });
            }
        };

        calculateTime();
        const interval = setInterval(calculateTime, 1000);
        return () => clearInterval(interval);
    }, [complaint]);

    // Get status badge
    const getStatusBadge = (status) => {
        const configs = {
            'pending': {
                icon: <Clock className="w-4 h-4" />,
                color: 'bg-amber-50 text-amber-700 border-amber-200',
                label: 'Pending'
            },
            'in-operation': {
                icon: <Activity className="w-4 h-4" />,
                color: 'bg-blue-50 text-blue-700 border-blue-200',
                label: 'In Progress'
            },
            'paused': {
                icon: <AlertCircle className="w-4 h-4" />,
                color: 'bg-gray-50 text-gray-600 border-gray-200',
                label: 'Paused'
            },
            'completed': {
                icon: <CheckCircle className="w-4 h-4" />,
                color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                label: 'Completed'
            }
        };
        const config = configs[status] || configs['pending'];
        return (
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${config.color}`}>
                {config.icon}
                {config.label}
            </span>
        );
    };

    // Render network status for Red Signal complaints
    const renderNetworkStatus = () => {
        if (complaint?.complaintType !== "Red signal") return null;

        return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                {/* ONU Status */}
                <div className="bg-blue-50/70 rounded-xl p-4 border border-blue-200">
                    <h4 className="text-sm font-medium text-blue-800 mb-3 flex items-center gap-2">
                        <Server className="w-4 h-4" />
                        ONU Status
                    </h4>
                    <div className="grid grid-cols-3 gap-3">
                        <div className="bg-white rounded-lg p-3 text-center">
                            <span className="text-gray-500 block text-[10px] uppercase tracking-wider">Power</span>
                            <span className={`font-bold text-lg ${getStatusValue(complaint.onu?.power) === 'on' ? 'text-green-600' : 'text-red-600'}`}>
                                {getStatusValue(complaint.onu?.power)}
                            </span>
                        </div>
                        <div className="bg-white rounded-lg p-3 text-center">
                            <span className="text-gray-500 block text-[10px] uppercase tracking-wider">PON</span>
                            <span className={`font-bold text-lg ${getStatusValue(complaint.onu?.pon) === 'green' ? 'text-green-600' : 'text-red-600'}`}>
                                {getStatusValue(complaint.onu?.pon)}
                            </span>
                        </div>
                        <div className="bg-white rounded-lg p-3 text-center">
                            <span className="text-gray-500 block text-[10px] uppercase tracking-wider">LAN</span>
                            <span className={`font-bold text-lg ${getStatusValue(complaint.onu?.lan) === 'on' ? 'text-green-600' : 'text-red-600'}`}>
                                {getStatusValue(complaint.onu?.lan)}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Router Status */}
                <div className="bg-purple-50/70 rounded-xl p-4 border border-purple-200">
                    <h4 className="text-sm font-medium text-purple-800 mb-3 flex items-center gap-2">
                        <Wifi className="w-4 h-4" />
                        Router Status
                    </h4>
                    <div className="grid grid-cols-3 gap-3">
                        <div className="bg-white rounded-lg p-3 text-center">
                            <span className="text-gray-500 block text-[10px] uppercase tracking-wider">Power</span>
                            <span className={`font-bold text-lg ${getStatusValue(complaint.router?.power) === 'on' ? 'text-green-600' : 'text-red-600'}`}>
                                {getStatusValue(complaint.router?.power)}
                            </span>
                        </div>
                        <div className="bg-white rounded-lg p-3 text-center">
                            <span className="text-gray-500 block text-[10px] uppercase tracking-wider">WLAN</span>
                            <span className={`font-bold text-lg ${getStatusValue(complaint.router?.wanLan) === 'on' ? 'text-green-600' : 'text-red-600'}`}>
                                {getStatusValue(complaint.router?.wanLan)}
                            </span>
                        </div>
                        <div className="bg-white rounded-lg p-3 text-center">
                            <span className="text-gray-500 block text-[10px] uppercase tracking-wider">WiFi</span>
                            <span className={`font-bold text-lg ${getStatusValue(complaint.router?.wifi) === 'on' ? 'text-green-600' : 'text-red-600'}`}>
                                {getStatusValue(complaint.router?.wifi)}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (loading) return <Loading />;
    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-md">
                    <div className="text-6xl mb-4">😕</div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Complaint Not Found</h2>
                    <p className="text-gray-600">{error}</p>
                </div>
            </div>
        );
    }

    const { customerInfo, status, ticketNumber, category, createdAt, approximateResolveTime, description, activityLogs } = complaint || {};

    const isCompleted = status === 'completed';
    const isInOperation = status === 'in-operation';

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4">
            <div className="max-w-3xl mx-auto">
                {/* Main Card */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden relative">
                    {/* Header - Clean and Simple */}
                    <div className="px-6 py-6 border-b border-gray-100 bg-gray-50/50">
                        <div className="flex flex-wrap justify-between items-center gap-4">
                            <div>
                                <h1 className="text-2xl font-semibold text-gray-800">Complaint Status</h1>
                                <p className="text-gray-500 text-sm mt-0.5">Track your complaint details below</p>
                            </div>
                            <div className="bg-gray-100 px-4 py-2 rounded-lg">
                                <span className="text-gray-700 font-mono text-sm font-medium">{ticketNumber}</span>
                            </div>
                        </div>
                    </div>

                    {/* Complaint Details */}
                    <div className="p-6 space-y-6">
                        {/* Status & Time */}
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            <div className="flex items-center gap-3 flex-wrap">
                                {getStatusBadge(status)}
                                {isDelayed && (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-rose-50 text-rose-700 border border-rose-200 rounded-full text-sm font-medium">
                                        <AlertTriangle className="w-4 h-4" />
                                        Delayed
                                    </span>
                                )}
                                {isPaused && (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 text-gray-600 border border-gray-200 rounded-full text-sm font-medium">
                                        <Clock className="w-4 h-4" />
                                        Paused (Non-Working Hours)
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                <Calendar className="w-4 h-4" />
                                {dayjs(createdAt?.toDate?.()).format("DD MMM YYYY")}
                            </div>
                        </div>

                        {/* Countdown Timer - Only for in-operation */}
                        {isInOperation && (
                            <div className={`rounded-xl p-4 border ${isPaused
                                ? 'bg-gray-50 border-gray-200'
                                : isDelayed
                                    ? 'bg-rose-50 border-rose-200'
                                    : timeRemaining?.hours < 1
                                        ? 'bg-amber-50 border-amber-200'
                                        : 'bg-blue-50 border-blue-200'
                                }`}>
                                <div className="flex items-center gap-4">
                                    <div className={`p-2.5 rounded-xl ${isPaused
                                        ? 'bg-gray-100 text-gray-600'
                                        : isDelayed
                                            ? 'bg-rose-100 text-rose-600'
                                            : timeRemaining?.hours < 1
                                                ? 'bg-amber-100 text-amber-600'
                                                : 'bg-blue-100 text-blue-600'
                                        }`}>
                                        <Timer className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-gray-600">
                                            {isPaused
                                                ? 'Outside Working Hours'
                                                : isDelayed
                                                    ? 'Complaint is Delayed'
                                                    : 'Time Remaining'}
                                        </p>
                                        <p className={`text-2xl font-semibold ${isPaused
                                            ? 'text-gray-600'
                                            : isDelayed
                                                ? 'text-rose-600'
                                                : timeRemaining?.hours < 1
                                                    ? 'text-amber-600'
                                                    : 'text-blue-600'
                                            }`}>
                                            {isPaused ? (
                                                <span className="flex items-center gap-2">
                                                    ⏸️ Resumes in {pauseRemaining?.hours > 0 && `${pauseRemaining.hours}h `}{pauseRemaining?.minutes}m {pauseRemaining?.seconds}s
                                                    <span className="text-sm font-normal text-gray-500">(9 AM - 10 PM)</span>
                                                </span>
                                            ) : isDelayed ? (
                                                <span className="flex items-center gap-2">
                                                    +{delayDuration?.hours}h {delayDuration?.minutes}m
                                                    <span className="text-sm font-normal text-rose-500">(Delayed)</span>
                                                </span>
                                            ) : timeRemaining ? (
                                                `${timeRemaining.hours > 0 ? timeRemaining.hours + 'h ' : ''}${timeRemaining.minutes}m ${timeRemaining.seconds}s`
                                            ) : (
                                                'Calculating...'
                                            )}
                                        </p>
                                        {!isPaused && !isDelayed && timeRemaining && (
                                            <p className="text-xs text-gray-500 mt-1">
                                                Working hours: 9 AM - 10 PM (Countdown pauses at night)
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Network Status for Red Signal */}
                        {renderNetworkStatus()}

                        {/* Customer Info */}
                        <div className="bg-gray-50 rounded-xl p-5">
                            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                                <User className="w-4 h-4 text-gray-400" />
                                Customer Information
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="flex items-center gap-2.5 text-sm text-gray-600">
                                    <User className="w-4 h-4 text-gray-400" />
                                    <span>{customerInfo?.name || 'N/A'}</span>
                                </div>
                                <div className="flex items-center gap-2.5 text-sm text-gray-600">
                                    <Phone className="w-4 h-4 text-gray-400" />
                                    <span>{customerInfo?.phone || 'N/A'}</span>
                                </div>
                                <div className="flex items-center gap-2.5 text-sm text-gray-600 md:col-span-2">
                                    <MapPin className="w-4 h-4 text-gray-400" />
                                    <span>{customerInfo?.address || 'N/A'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Complaint Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-gray-50 rounded-xl p-5">
                                <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                                    <Tag className="w-4 h-4 text-gray-400" />
                                    Category
                                </h3>
                                <p className="text-gray-600">{category || 'N/A'}</p>
                            </div>
                            <div className="bg-gray-50 rounded-xl p-5">
                                <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                                    <Clock className="w-4 h-4 text-gray-400" />
                                    Estimated Resolution
                                </h3>
                                <p className="text-gray-600">{approximateResolveTime || 'N/A'}</p>
                            </div>
                        </div>

                        {/* Description */}
                        {description && (
                            <div className="bg-gray-50 rounded-xl p-5">
                                <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-gray-400" />
                                    Description
                                </h3>
                                <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
                            </div>
                        )}

                        {/* Activity Logs */}
                        {activityLogs?.length > 0 && (
                            <div className="border-t border-gray-100 pt-5">
                                <h3 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
                                    <Activity className="w-4 h-4 text-gray-400" />
                                    Activity Timeline
                                </h3>
                                <div className="space-y-3">
                                    {activityLogs.map((log, index) => (
                                        <div key={index} className="flex gap-3">
                                            <div className="flex flex-col items-center">
                                                <div className={`w-2.5 h-2.5 rounded-full ${index === 0 ? 'bg-blue-500' : 'bg-gray-300'}`} />
                                                {index < activityLogs.length - 1 && (
                                                    <div className="w-0.5 h-full bg-gray-200" />
                                                )}
                                            </div>
                                            <div className="flex-1 pb-3">
                                                <p className="text-sm text-gray-700">
                                                    {log.message.includes('approximateResolveTime')
                                                        ? log.message.replace('approximateResolveTime', 'Approximate Resolution Time')
                                                        : log.message}
                                                </p>
                                                <p className="text-xs text-gray-400 mt-0.5">
                                                    {dayjs(log.time?.toDate?.()).format("DD MMM YYYY, h:mm A")}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Completed Status */}
                        {isCompleted && (
                            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center">
                                <div className="flex items-center justify-center gap-2 text-emerald-700">
                                    <CheckCheck className="w-6 h-6" />
                                    <span className="font-semibold">This complaint has been resolved</span>
                                </div>
                                {complaint.completionComment && (
                                    <p className="text-sm text-emerald-600 mt-1">{complaint.completionComment}</p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 relative">
                        <div className="text-center">
                            <p className="text-sm text-gray-600 font-medium">Bijoy IT Support Team</p>
                            <p className="text-xs text-gray-400 mt-1">support@bijoyit.com</p>
                        </div>
                        <button
                            onClick={() => window.print()}
                            className="absolute right-6 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 transition rounded-lg hover:bg-gray-100"
                            aria-label="Print"
                        >
                            <PrinterIcon className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ComplaintStatus;